import { domainError, SHARED_ERROR, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

/** Dia da semana ISO: 1 = segunda … 7 = domingo. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * Data de negócio — um dia no calendário da obra.
 *
 * Representada como `"YYYY-MM-DD"`, **sem hora e sem fuso** (RN-120, ADR-007).
 * "Necessário em 25/08" não pode virar 24/08 porque o dispositivo está em UTC−3.
 *
 * A aritmética interna usa `Date.UTC` apenas como contador de dias — nunca há
 * conversão para o fuso local. A string também ordena cronologicamente por
 * comparação lexicográfica, o que a torna ideal para índice do IndexedDB.
 */
export class BusinessDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;

  private constructor(year: number, month: number, day: number) {
    this.year = year;
    this.month = month;
    this.day = day;
  }

  /** Aceita apenas `"YYYY-MM-DD"` que exista de fato no calendário. */
  static parse(value: string): Result<BusinessDate, DomainError> {
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match) {
      return err(
        domainError(SHARED_ERROR.INVALID_DATE, 'Data deve estar no formato AAAA-MM-DD.', {
          value,
        }),
      );
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return BusinessDate.fromParts(year, month, day);
  }

  static fromParts(year: number, month: number, day: number): Result<BusinessDate, DomainError> {
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return err(
        domainError(SHARED_ERROR.INVALID_DATE, 'Data inexistente no calendário.', {
          year,
          month,
          day,
        }),
      );
    }
    // Rejeita 31/02 e 29/02 fora de ano bissexto: o UTC normalizaria em silêncio.
    const utc = new Date(Date.UTC(year, month - 1, day));
    if (
      utc.getUTCFullYear() !== year ||
      utc.getUTCMonth() !== month - 1 ||
      utc.getUTCDate() !== day
    ) {
      return err(
        domainError(SHARED_ERROR.INVALID_DATE, 'Data inexistente no calendário.', {
          year,
          month,
          day,
        }),
      );
    }
    return ok(new BusinessDate(year, month, day));
  }

  /** Para literais confiáveis (constantes, seed, teste). Lança se inválido. */
  static of(value: string): BusinessDate {
    const result = BusinessDate.parse(value);
    if (!result.ok) throw new Error(`${result.error.message} (recebido: ${value})`);
    return result.value;
  }

  /**
   * Converte um instante para o dia-calendário correspondente **no fuso informado**.
   * É assim que se obtém o "hoje da obra" (RN-123): o instante vem do `Clock`, o fuso
   * vem de `Project.timezone`.
   */
  static fromInstant(instant: Date, timeZone: string): BusinessDate {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(instant);

    const get = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((part) => part.type === type)?.value ?? Number.NaN);

    const result = BusinessDate.fromParts(get('year'), get('month'), get('day'));
    if (!result.ok) {
      throw new Error(`Fuso horário inválido ou instante irrepresentável: ${timeZone}`);
    }
    return result.value;
  }

  private static fromUtcMillis(millis: number): BusinessDate {
    const utc = new Date(millis);
    return new BusinessDate(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
  }

  /** Milissegundos UTC do início do dia — contador interno, nunca exposto como instante. */
  private toUtcMillis(): number {
    return Date.UTC(this.year, this.month - 1, this.day);
  }

  get value(): string {
    return `${String(this.year).padStart(4, '0')}-${String(this.month).padStart(2, '0')}-${String(
      this.day,
    ).padStart(2, '0')}`;
  }

  addDays(days: number): BusinessDate {
    return BusinessDate.fromUtcMillis(this.toUtcMillis() + days * MS_PER_DAY);
  }

  subtractDays(days: number): BusinessDate {
    return this.addDays(-days);
  }

  /** Dias de `this` até `other`. Negativo quando `other` é anterior. */
  daysUntil(other: BusinessDate): number {
    return Math.round((other.toUtcMillis() - this.toUtcMillis()) / MS_PER_DAY);
  }

  weekday(): IsoWeekday {
    const day = new Date(this.toUtcMillis()).getUTCDay();
    return (day === 0 ? 7 : day) as IsoWeekday;
  }

  /** Chave de agrupamento mensal — `"2026-08"` (RN-124). */
  monthKey(): string {
    return `${String(this.year).padStart(4, '0')}-${String(this.month).padStart(2, '0')}`;
  }

  startOfMonth(): BusinessDate {
    return new BusinessDate(this.year, this.month, 1);
  }

  endOfMonth(): BusinessDate {
    return BusinessDate.fromUtcMillis(Date.UTC(this.year, this.month, 1) - MS_PER_DAY);
  }

  compare(other: BusinessDate): number {
    return this.toUtcMillis() - other.toUtcMillis();
  }

  equals(other: BusinessDate): boolean {
    return this.value === other.value;
  }

  isBefore(other: BusinessDate): boolean {
    return this.compare(other) < 0;
  }

  isAfter(other: BusinessDate): boolean {
    return this.compare(other) > 0;
  }

  isBetween(start: BusinessDate, end: BusinessDate): boolean {
    return this.compare(start) >= 0 && this.compare(end) <= 0;
  }

  /** `"25/08/2026"` — formato brasileiro, só para exibição. */
  toDisplay(): string {
    return `${String(this.day).padStart(2, '0')}/${String(this.month).padStart(2, '0')}/${this.year}`;
  }

  toString(): string {
    return this.value;
  }
}

/** Todos os dias do intervalo, inclusive nas duas pontas. */
export function eachDayOfRange(
  start: BusinessDate,
  end: BusinessDate,
): Result<BusinessDate[], DomainError> {
  if (start.isAfter(end)) {
    return err(
      domainError(SHARED_ERROR.INVALID_RANGE, 'A data inicial é posterior à data final.', {
        start: start.value,
        end: end.value,
      }),
    );
  }
  const days: BusinessDate[] = [];
  let current = start;
  while (!current.isAfter(end)) {
    days.push(current);
    current = current.addDays(1);
  }
  return ok(days);
}
